import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Skeleton,
  IconButton,
  Button,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import GradientButton from "../common/GradiantButton";
import { useSelector } from "react-redux";
import { vendorApi } from "../../api/api";
 
const YourProjectDocuments = ({ data }) => {
  const [allDocuments, setAllDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const theme = useSelector((state) => state.themeValue.colors);
 
  useEffect(() => {
    if (!data) return;
 
    const typeMap = {
      1: "project",
      2: "property",
      3: "apartment",
      4: "individual villa",
    };
 
    const payload = {
      propertyId: data.id,
      PropertyType: typeMap[data.type],
    };
 
    const fetchDocuments = async () => {
      try {
        const response = await vendorApi.getDocumentsById(payload);
        const cleaned = response.data.map((doc) => ({
          ...doc,
          DocumentURL: doc.DocumentURL?.replace(/\\/g, ""),
        }));
        setAllDocuments(cleaned);
      } catch (error) {
        console.error("Failed to fetch documents:", error);
      } finally {
        setLoading(false);
      }
    };
 
    fetchDocuments();
  }, [data]);
 
  const openPdf = (url) => {
    if (url?.startsWith("http")) {
      window.open(url, "_blank");
    } else {
      alert("Invalid document URL.");
    }
  };
 
  const handleDelete = async(id) => {
     const confirmDelete = window.confirm("Are you sure you want to delete this document?");
     if (!confirmDelete) return;
     try {
       const response = await vendorApi.deleteDocumentById({
        id:id,
        type:data.type
       });
   
       if (response.status === 200) {
         setAllDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== id));
         alert("Document deleted successfully");
       } else {
         alert("Failed to delete document");
       }
     } catch (error) {
       console.error("Error deleting document:", error);
       alert("Error occurred while deleting the document.");
     }
  };
 
  const handleAddDocument = () => {
    navigate("/addProjectDocuments", { state: { data } });
  };
 
  const documentsToShow = showAll ? allDocuments : allDocuments.slice(0, 3);
 
  return (
    <Box sx={{ mt: 8, backgroundColor: theme.background, p: 2, borderRadius: "12px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography  fontWeight={600} sx={{ color: theme.text,fontSize:"20px" }}>
          Project Documents
        </Typography>
 
        <GradientButton
          text="Add Documents"
          onClick={handleAddDocument}
          width="180px"
          height={36}
        />
      </Box>
 
      <Card variant="outlined" sx={{ borderRadius: 2, p: 2, backgroundColor: theme.background }}>
        <CardContent>
          {loading ? (
            <Stack spacing={2}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={40} />
              ))}
            </Stack>
          ) : allDocuments.length > 0 ? (
            <>
              {documentsToShow.map((doc) => (
                <Box
                  key={doc.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                    mb: 1,
                    borderRadius: 1,
                    backgroundColor: theme.background,
                    border: `1px solid ${theme.borderColor}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PictureAsPdfIcon sx={{ color: theme.normal_icon }} />
                    <Typography sx={{ color: theme.text }}>
                      {doc.DocumentName}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      onClick={() => openPdf(doc.DocumentURL)}
                      aria-label="view document"
                      size="small"
                      sx={{ color: theme.normal_icon }}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(doc.id)}
                      aria-label="delete document"
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
 
              {allDocuments.length > 3 && (
                <Button
                  onClick={() => setShowAll(!showAll)}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ mt: 2 }}
                >
                  {showAll ? "Show Less" : "View All Documents"}
                </Button>
              )}
            </>
          ) : (
            <Typography color="text.secondary">No Documents Available</Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
 
export default YourProjectDocuments;